import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import { Producent, Kategori, hämtaProducenter, hämtaKategorier, skapaProducent, datumText, felText } from '../api/inkop';
import { sida, rubrik, kort, fält, knapp, knappSpök, etikett, tabell, th, td, tal } from '../components/inkopStil';

// Två flikar med samma logik som kund och prospect i Restaurang: en kandidat
// blir producent av sig själv när det finns artiklar från den i Fortnox.
type Flik = 'producenter' | 'kandidater';

function flikStil(aktiv: boolean): React.CSSProperties {
  return {
    padding: '8px 2px', marginRight: '22px', background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: "'Inter', sans-serif", fontSize: '14px', color: aktiv ? TEMA.ink : TEMA.inkFaint,
    borderBottom: `2px solid ${aktiv ? TEMA.accent : 'transparent'}`,
  };
}

export function KategoriEtikett({ k }: { k: Kategori }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: '11px', padding: '1px 8px', borderRadius: '10px', marginRight: '4px',
      background: `${k.farg}2E`, color: k.farg, whiteSpace: 'nowrap',
    }}>{k.namn}</span>
  );
}

export default function InkopProducenter() {
  const navigate = useNavigate();
  const ärMobil = useMobil();
  const [producenter, setProducenter] = useState<Producent[] | null>(null);
  const [kategorier, setKategorier] = useState<Kategori[]>([]);
  const [sök, setSök] = useState('');
  const [kategori, setKategori] = useState('');
  const [flik, setFlik] = useState<Flik>('producenter');
  const [fel, setFel] = useState<string | null>(null);
  const [ny, setNy] = useState(false);
  const [namn, setNamn] = useState('');
  const [land, setLand] = useState('');
  const [valuta, setValuta] = useState('EUR');

  useEffect(() => {
    hämtaProducenter().then(r => setProducenter(r.data)).catch(e => setFel(felText(e, 'Kunde inte hämta producenterna.')));
    hämtaKategorier().then(r => setKategorier(r.data)).catch(() => {});
  }, []);

  const antal = useMemo(() => ({
    producenter: (producenter || []).filter(p => !p.kandidat).length,
    kandidater: (producenter || []).filter(p => p.kandidat).length,
  }), [producenter]);

  const synliga = useMemo(() => (producenter || []).filter(p =>
    (flik === 'kandidater') === !!p.kandidat
    && (!sök || `${p.namn} ${p.land || ''} ${p.region || ''}`.toLowerCase().includes(sök.toLowerCase()))
    && (!kategori || p.kategorier.some(k => k.id === kategori)),
  ), [producenter, sök, kategori, flik]);

  async function skapa(e: React.FormEvent) {
    e.preventDefault();
    try {
      const res = await skapaProducent({ namn, land: land || undefined, valuta });
      navigate(`/inkop/producenter/${res.data.id}`);
    } catch (err) {
      setFel(felText(err, 'Kunde inte skapa producenten.'));
    }
  }

  return (
    <div style={sida}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <h1 style={rubrik}>Producenter</h1>
        <button style={knapp} onClick={() => setNy(n => !n)}>+ Ny kandidat</button>
      </div>

      {ny && (
        <form onSubmit={skapa} style={{ ...kort, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '14px', alignItems: 'end' }}>
          <div>
            <label style={etikett} htmlFor="p-namn">Namn</label>
            <input id="p-namn" style={fält} value={namn} onChange={e => setNamn(e.target.value)} required />
          </div>
          <div>
            <label style={etikett} htmlFor="p-land">Land</label>
            <input id="p-land" style={fält} value={land} onChange={e => setLand(e.target.value)} placeholder="Frankrike" />
          </div>
          <div>
            <label style={etikett} htmlFor="p-valuta">Valuta</label>
            <select id="p-valuta" style={fält} value={valuta} onChange={e => setValuta(e.target.value)}>
              <option>EUR</option><option>USD</option><option>SEK</option><option>GBP</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" style={knapp}>Skapa</button>
            <button type="button" style={knappSpök} onClick={() => setNy(false)}>Avbryt</button>
          </div>
        </form>
      )}

      <div style={{ borderBottom: `1px solid ${TEMA.border}`, marginBottom: '16px' }}>
        <button style={flikStil(flik === 'producenter')} onClick={() => setFlik('producenter')}>Producenter {antal.producenter}</button>
        <button style={flikStil(flik === 'kandidater')} onClick={() => setFlik('kandidater')}>Kandidater {antal.kandidater}</button>
      </div>
      {flik === 'kandidater' && (
        <p style={{ fontSize: '13px', margin: '0 0 14px', maxWidth: '680px', lineHeight: 1.5 }}>
          Producenter vi för dialog med men ännu inte har några artiklar från i Fortnox. En kandidat blir
          producent av sig själv när det finns en artikel vars tillverkare stämmer med namnet under Redigera.
        </p>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input style={{ ...fält, maxWidth: '280px' }} placeholder="Sök producent, land eller region" value={sök} onChange={e => setSök(e.target.value)} />
        {kategorier.length > 0 && (
          <select style={{ ...fält, maxWidth: '200px' }} value={kategori} onChange={e => setKategori(e.target.value)} aria-label="Kategori">
            <option value="">Alla kategorier</option>
            {kategorier.map(k => <option key={k.id} value={k.id}>{k.namn}</option>)}
          </select>
        )}
      </div>

      {fel && <p style={{ color: '#D9695F', fontSize: '13px' }}>{fel}</p>}
      {!producenter && !fel && <p>Laddar...</p>}
      {producenter && synliga.length === 0 && (
        <p style={{ fontSize: '14px' }}>
          {flik === 'kandidater' ? 'Inga kandidater just nu.' : 'Inga producenter än.'}
        </p>
      )}

      {/* På mobil blir varje producent ett kort: namn och plats, sedan inköpen. */}
      {synliga.length > 0 && ärMobil && (
        <div>
          {synliga.map(p => (
            <Link key={p.id} to={`/inkop/producenter/${p.id}`} style={{
              display: 'block', textDecoration: 'none', padding: '12px 0', borderBottom: `1px solid ${TEMA.border}`, opacity: p.aktiv ? 1 : 0.5,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px' }}>
                <span style={{ fontSize: '14.5px', color: TEMA.ink, minWidth: 0 }}>{p.namn}</span>
                {p.pagaende ? <span style={{ ...tal, fontSize: '12.5px', color: TEMA.accent }}>{p.pagaende} pågående</span> : null}
              </div>
              <div style={{ fontSize: '12.5px', color: TEMA.inkFaint, marginTop: '2px' }}>
                {[p.region, p.land].filter(Boolean).join(', ') || 'Land saknas'}
                {' · '}{p.antal_inkop ? `${p.antal_inkop} ${p.antal_inkop === 1 ? 'inköp' : 'inköp'}, senast ${datumText(p.senaste_inkop)}` : 'inga inköp än'}
              </div>
              {p.kategorier.length > 0 && <div style={{ marginTop: '6px' }}>{p.kategorier.map(k => <KategoriEtikett key={k.id} k={k} />)}</div>}
            </Link>
          ))}
        </div>
      )}

      {synliga.length > 0 && !ärMobil && (
        <div style={{ overflowX: 'auto' }}>
          <table style={tabell}>
            <thead>
              <tr>
                <th style={th}>{flik === 'kandidater' ? 'Kandidat' : 'Producent'}</th>
                <th style={th}>Land</th>
                <th style={th}>Kategorier</th>
                <th style={{ ...th, ...tal }}>Inköp</th>
                <th style={{ ...th, ...tal }}>Pågående</th>
                <th style={th}>Senaste inköp</th>
              </tr>
            </thead>
            <tbody>
              {synliga.map(p => (
                <tr key={p.id} onClick={() => navigate(`/inkop/producenter/${p.id}`)} style={{ cursor: 'pointer', opacity: p.aktiv ? 1 : 0.5 }}>
                  <td style={td}>{p.namn}</td>
                  <td style={{ ...td, color: TEMA.inkSoft }}>{[p.region, p.land].filter(Boolean).join(', ') || '–'}</td>
                  <td style={td}>{p.kategorier.map(k => <KategoriEtikett key={k.id} k={k} />)}</td>
                  <td style={{ ...td, ...tal }}>{p.antal_inkop || 0}</td>
                  <td style={{ ...td, ...tal, color: p.pagaende ? TEMA.accent : TEMA.inkFaint }}>{p.pagaende || 0}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{datumText(p.senaste_inkop)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
